/**
 * QuoteDiscountsSection.tsx
 *
 * The Discounts band. Each discount is either a flat amount or a percentage,
 * and applies to one part of the quote — "Applies to" is legacy's quoteElement.
 *
 * Amount, Type and Applies to are money fields gated on user level. Legacy
 * renders all three disabled on load (quote-discounts-display-new.php:173,
 * :193, :211) and marks them `.nonAdminEdit`; allowAdminEdit then re-enables
 * them for level 3 and up, or at a facility with nonAdminAllowEdit
 * (quote-handling-ds.js:1424-1442). The editor passes that same rule in as
 * `pricingReadOnly`.
 */
import { useQuery } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { getDiscountCatalog, type CatalogDiscount, type QuoteDiscount } from '@/services/quotes.service';
import { QuoteSectionCard, EmptySectionRow } from './QuoteSectionCard';
import { DraftNumberInput } from './DraftNumberInput';
import {
  DISCOUNT_SCOPE_OPTIONS,
  DISCOUNT_TYPE_OPTIONS,
  DEFAULT_DISCOUNT_IS_PERCENT,
  DEFAULT_DISCOUNT_SCOPE,
} from '../discountOptions';
import { AutoGrowTextarea } from '@/components/ui/Textarea';

interface Props {
  discounts: Partial<QuoteDiscount>[];
  readOnly: boolean;
  /** Amount, Type and Applies to follow the pricing gate. */
  pricingReadOnly?: boolean;
  onChange: (discounts: Partial<QuoteDiscount>[]) => void;
}

export function QuoteDiscountsSection({ discounts, readOnly, pricingReadOnly = false, onChange }: Props) {
  const { data: catalog } = useQuery({
    queryKey: ['quote-discount-catalog'],
    queryFn: getDiscountCatalog,
    staleTime: 300_000,
  });

  function patch(index: number, changes: Partial<QuoteDiscount>) {
    onChange(discounts.map((row, i) => (i === index ? { ...row, ...changes } : row)));
  }

  function add() {
    onChange([
      ...discounts,
      {
        discountId: 0,
        name: '',
        description: '',
        isPercent: DEFAULT_DISCOUNT_IS_PERCENT,
        quoteElement: DEFAULT_DISCOUNT_SCOPE,
        amount: 0,
        active: true,
      },
    ]);
  }

  const field = 'rounded-md border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100';

  return (
    <QuoteSectionCard title="Discounts" onAdd={readOnly ? undefined : add} addLabel="Add">
      {discounts.length === 0 ? (
        <EmptySectionRow>No discounts on this quote.</EmptySectionRow>
      ) : (
        <div className="space-y-3">
          {discounts.map((row, index) => (
            <div key={row.quoteDiscountId ?? `new-${index}`} className="rounded-md border border-slate-200 p-3">
              <div className="grid gap-2 md:grid-cols-[2fr_auto_auto_auto_auto] max-md:grid-cols-1">
                <label className="text-xs text-slate-600">
                  Discount
                  <select
                    className={`${field} w-full`}
                    value={row.discountId || ''}
                    disabled={readOnly}
                    onChange={(e) => {
                      const id = e.target.value ? Number(e.target.value) : 0;
                      const chosen = (catalog ?? []).find((c: CatalogDiscount) => c.discountId === id);

                      // The catalog seeds the row, then each field is the
                      // coordinator's to override on this quote.
                      patch(index, {
                        discountId: id,
                        ...(chosen
                          ? {
                              name: chosen.name,
                              description: chosen.description,
                              isPercent: chosen.isPercent,
                              quoteElement: chosen.quoteElement,
                              amount: chosen.amount,
                            }
                          : {}),
                      });
                    }}
                  >
                    <option value="">Select one…</option>
                    {(catalog ?? []).map((c: CatalogDiscount) => (
                      <option key={c.discountId} value={c.discountId}>{c.name}</option>
                    ))}
                  </select>
                </label>

                <label className="text-xs text-slate-600">
                  Amount
                  {/* Never negative: the stored amount cannot be (SCRUM-2088). */}
                  <DraftNumberInput
                    className={`${field} w-24 text-right`}
                    allowNegative={false}
                    value={row.amount ?? 0}
                    disabled={readOnly || pricingReadOnly}
                    onCommit={(amount) => patch(index, { amount })}
                  />
                </label>

                <label className="text-xs text-slate-600">
                  Type
                  <select
                    className={field}
                    value={row.isPercent ? '1' : '0'}
                    disabled={readOnly || pricingReadOnly}
                    onChange={(e) => patch(index, { isPercent: e.target.value === '1' })}
                  >
                    {DISCOUNT_TYPE_OPTIONS.map((t) => (
                      <option key={t.label} value={t.isPercent ? '1' : '0'}>{t.label}</option>
                    ))}
                  </select>
                </label>

                <label className="text-xs text-slate-600">
                  Applies to
                  <select
                    className={field}
                    value={row.quoteElement ?? 'all'}
                    disabled={readOnly || pricingReadOnly}
                    onChange={(e) => patch(index, { quoteElement: e.target.value })}
                  >
                    {DISCOUNT_SCOPE_OPTIONS.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </label>

                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => onChange(discounts.filter((_, i) => i !== index))}
                    className="mb-1 self-end rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 touch-target-44 tap-feedback"
                    aria-label="Remove discount"
                  >
                    <Trash2 className="h-4 w-4 touch-target-icon" />
                  </button>
                )}
              </div>

              <label className="mt-2 block text-xs text-slate-600">
                Description
                <AutoGrowTextarea
                  className={`${field} w-full`}
                  rows={2}
                  value={row.description ?? ''}
                  disabled={readOnly}
                  onChange={(e) => patch(index, { description: e.target.value })}
                />
              </label>
            </div>
          ))}
        </div>
      )}
    </QuoteSectionCard>
  );
}
